import { useEffect, useState } from 'react';
import { ROUTING_POLICY_NAMES, type RoutingPolicy, type RoutingRule } from '@trivio/contracts';
import { Button } from '@tervior-tech/tervior-shared-ui-react';
import { useDashboardSessionKey } from '../../app/DashboardSessionKeyContext';
import { ApiProblemError, getRoutingPolicy, putRoutingPolicy, type ProblemFieldError } from '../../lib/api-client';
import { Card } from '../../components/ui/Card';
import { Label } from '../../components/ui/Label';
import { Alert } from '../../components/ui/Alert';
import { LoadingState } from '../../components/ui/Spinner';
import { RuleEditor, type RuleFieldErrors } from './RuleEditor';
import { MAX_RULES, emptyRule, routingPolicyNamePt } from './policySchema';

/**
 * Maps `errors[].field` (e.g. `rules[2].match.weight_g_min`,
 * `rules[0].action`) back onto the rule index/section that produced it, so
 * a `422` from `PUT /routing-policy` (`validateRoutingPolicy`,
 * apps/api/src/modules/routing/policy.schema.ts) highlights the exact rule
 * and field instead of just a generic top-level message. Anything that
 * doesn't match the `rules[N].xxx` shape (e.g. `default_policy`) falls
 * through to `top`.
 */
function parseFieldErrors(errors: ProblemFieldError[]): { top: string[]; byRule: Record<number, RuleFieldErrors> } {
  const top: string[] = [];
  const byRule: Record<number, RuleFieldErrors> = {};
  const pattern = /^rules\[(\d+)\]\.(match|action)(?:\.(\w+))?$/;

  for (const err of errors) {
    const match = pattern.exec(err.field);
    if (!match) {
      top.push(err.message);
      continue;
    }
    const index = Number(match[1]);
    const group = match[2];
    const subfield = match[3];
    const entry = byRule[index] ?? {};
    if (group === 'match') {
      if (subfield === 'weight_g_min') {
        entry.weightMin = err.message;
      } else {
        entry.match = err.message;
      }
    } else {
      entry.action = err.message;
    }
    byRule[index] = entry;
  }

  return { top, byRule };
}

function extractProblemMessage(err: unknown, fallback: string): string {
  return err instanceof ApiProblemError ? (err.problem.detail ?? err.problem.title) : fallback;
}

/**
 * `/routing` (TTP-244, R4) — editor for the org's `RoutingPolicy`. `Salvar`
 * always sends the FULL policy via `PUT` (the contract replaces it
 * wholesale — no partial PATCH, see `policySchema.ts`'s docstring and
 * `api-client.ts#putRoutingPolicy`).
 */
export function RoutingPolicyPage() {
  const { key } = useDashboardSessionKey();
  const apiKey = key?.key ?? null;

  const [policy, setPolicy] = useState<RoutingPolicy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [ruleFieldErrors, setRuleFieldErrors] = useState<Record<number, RuleFieldErrors>>({});

  useEffect(() => {
    if (!apiKey) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getRoutingPolicy(apiKey)
      .then((result) => {
        if (!cancelled) setPolicy(result);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(extractProblemMessage(err, 'Não foi possível carregar a política de roteamento.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiKey]);

  function updateRule(index: number, next: RoutingRule) {
    if (!policy) return;
    const rules = [...policy.rules];
    rules[index] = next;
    setPolicy({ ...policy, rules });
  }

  function removeRule(index: number) {
    if (!policy) return;
    setPolicy({ ...policy, rules: policy.rules.filter((_, i) => i !== index) });
  }

  function moveRule(index: number, direction: -1 | 1) {
    if (!policy) return;
    const target = index + direction;
    if (target < 0 || target >= policy.rules.length) return;
    const rules = [...policy.rules];
    const moved = rules[index]!;
    rules.splice(index, 1);
    rules.splice(target, 0, moved);
    setPolicy({ ...policy, rules });
  }

  function addRule() {
    if (!policy || policy.rules.length >= MAX_RULES) return;
    setPolicy({ ...policy, rules: [...policy.rules, emptyRule()] });
  }

  async function handleSave() {
    if (!apiKey || !policy) return;
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    setRuleFieldErrors({});
    try {
      const saved = await putRoutingPolicy(apiKey, policy);
      setPolicy(saved);
      setSaveSuccess(true);
    } catch (err) {
      if (err instanceof ApiProblemError && err.problem.errors && err.problem.errors.length > 0) {
        const { top, byRule } = parseFieldErrors(err.problem.errors);
        setRuleFieldErrors(byRule);
        const detail = err.problem.detail ?? err.problem.title;
        setSaveError([detail, ...top].filter(Boolean).join(' — '));
      } else {
        setSaveError(extractProblemMessage(err, 'Não foi possível salvar a política agora.'));
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-medium text-foreground">Roteamento</h1>
        <p className="text-sm text-muted-foreground">
          Defina a política padrão e regras por CEP/peso — a primeira regra que casar vence (R4).
        </p>
      </div>

      {error ? (
        <Alert variant="error" title="Não foi possível carregar a política">
          {error}
        </Alert>
      ) : null}
      {saveError ? (
        <Alert variant="error" title="Não foi possível salvar">
          {saveError}
        </Alert>
      ) : null}
      {saveSuccess ? (
        <Alert variant="info" title="Política salva">
          A política de roteamento foi atualizada.
        </Alert>
      ) : null}

      {loading ? (
        <LoadingState label="Carregando política de roteamento…" />
      ) : policy ? (
        <>
          <Card className="flex flex-col gap-3 p-6">
            <Label htmlFor="default-policy">Política padrão</Label>
            <select
              id="default-policy"
              value={policy.default_policy}
              onChange={(event) =>
                setPolicy({ ...policy, default_policy: event.target.value as RoutingPolicy['default_policy'] })
              }
              className="w-full max-w-xs rounded-none border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            >
              {ROUTING_POLICY_NAMES.map((name) => (
                <option key={name} value={name}>
                  {routingPolicyNamePt(name)}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">Usada quando nenhuma regra abaixo casar com o envio.</p>
          </Card>

          <div className="border border-border">
            <div className="flex items-center justify-between gap-4 border-b border-border p-4">
              <h2 className="text-lg font-medium text-foreground">
                Regras ({policy.rules.length}/{MAX_RULES})
              </h2>
              <Button type="button" variant="outline" onClick={addRule} disabled={policy.rules.length >= MAX_RULES}>
                Adicionar regra
              </Button>
            </div>

            {policy.rules.length === 0 ? (
              <p className="p-4 text-sm text-muted-foreground">Nenhuma regra — todo envio usa a política padrão acima.</p>
            ) : (
              <div className="flex flex-col gap-4 p-4">
                {policy.rules.map((rule, index) => (
                  <RuleEditor
                    key={index}
                    rule={rule}
                    index={index}
                    total={policy.rules.length}
                    {...(ruleFieldErrors[index] ? { fieldErrors: ruleFieldErrors[index] } : {})}
                    onChange={(next) => updateRule(index, next)}
                    onRemove={() => removeRule(index)}
                    onMoveUp={() => moveRule(index, -1)}
                    onMoveDown={() => moveRule(index, 1)}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end border-t border-border pt-6">
            <Button type="button" variant="default" onClick={handleSave} disabled={saving}>
              {saving ? 'Salvando…' : 'Salvar'}
            </Button>
          </div>
        </>
      ) : null}
    </div>
  );
}
